import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "components/breadcrumb";
import articleService from "service/article.service";
import EditArticlePage from "components/articles/EditArticleModals";

const ArticlesEditsView = () => {
  const { id_art } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id_art) {
        setError("Identifiant d'article manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await articleService.getAllById(id_art);
        const articleData = response.data || response;

        // Ajouter les métadonnées de modification
        setArticle({
          ...articleData,
          id_art: id_art,
        });
      } catch (err) {
        console.error("Erreur lors de la récupération de l'article:", err);
        setError("Impossible de charger l'article demandé");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id_art]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleSuccess = (updatedArticle) => {
    // Rediriger vers la page de l'article mis à jour
    navigate(`/admin/article/details/${updatedArticle}`, {
      state: { message: "Article mis à jour avec succès" },
    });
  };

  // État de chargement
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: "Tableau de bord", link: "/admin/dashboard" },
            { label: "Articles", link: "/admin/articles" },
            { label: "Chargement...", link: "#" },
          ]}
        />
        <div className="flex h-[60vh] w-full flex-col items-center justify-center space-y-6">
          <div className="relative">
            <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-[#57A863]"></div>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-700">
              Chargement de l'article
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Veuillez patienter pendant que nous récupérons les données...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: "Tableau de bord", link: "/admin/default" },
            { label: "Articles", link: "/admin/articles/publies" },
            { label: "Erreur", link: "#" },
          ]}
        />
        <div className="mx-auto mt-10 max-w-2xl rounded-lg border border-red-100 bg-red-50 p-6 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            <svg
              className="h-12 w-12 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-red-700">{error}</h3>
          <p className="mb-4 text-sm text-red-600">
            Une erreur est survenue lors du chargement de l'article. Veuillez
            réessayer.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate("/admin/articles")}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
            >
              Retour à la liste
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Article non trouvé
  if (!article) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: "Tableau de bord", link: "/admin/default" },
            { label: "Articles", link: "/admin/articles/publies" },
            { label: "Article introuvable", link: "#" },
          ]}
        />
        <div className="mx-auto mt-10 max-w-2xl rounded-lg border border-yellow-100 bg-yellow-50 p-6 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            <svg
              className="h-12 w-12 text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-yellow-700">
            Article introuvable
          </h3>
          <p className="mb-4 text-sm text-yellow-600">
            L'article que vous essayez de modifier n'existe pas ou a été
            supprimé.
          </p>
          <button
            onClick={() => navigate("/admin/articles")}
            className="rounded-md bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-yellow-700"
          >
            Retour à la liste des articles
          </button>
        </div>
      </div>
    );
  }

  // Rendu principal
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb
        items={[
          { label: "Tableau de bord", link: "/admin/default" },
          { label: "Articles", link: "/admin/articles/publies" },
          {
            label: `Modification: ${article.title_art?.fr || article.slug}`,
            link: "#",
          },
        ]}
      />

      <EditArticlePage
        article={article}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default ArticlesEditsView;
